import Api from '../API';
import RecruitApiClient from '../../../RecruitApiClient';

// Mock the RecruitApiClient
jest.mock('../../../RecruitApiClient');
const mockRecruitApiClient = RecruitApiClient as jest.Mocked<typeof RecruitApiClient>;

describe('Candidates API', () => {
  beforeEach(() => {
    jest.clearAllMocks();
  });

  describe('getCandidates', () => {
    it('should call RecruitApiClient.get with correct parameters', () => {
      const params = {
        page: 1,
        limit: 10,
        sort: 'firstname',
        sort_order: 'asc',
        'q[search]': 'test search'
      };

      mockRecruitApiClient.get.mockResolvedValue({ data: [] });

      Api.getCandidates(params);

      expect(mockRecruitApiClient.get).toHaveBeenCalledWith('/api/v4/candidates', params);
    });
  });

  describe('getPreferences', () => {
    it('should call RecruitApiClient.get with correct endpoint', () => {
      mockRecruitApiClient.get.mockResolvedValue({ data: [] });

      Api.getPreferences();

      expect(mockRecruitApiClient.get).toHaveBeenCalledWith(
        '/api/v4/ui_preferences?source=candidates'
      );
    });
  });

  describe('getDensityPreferences', () => {
    it('should call RecruitApiClient.get with correct endpoint', () => {
      mockRecruitApiClient.get.mockResolvedValue({ data: [] });

      Api.getDensityPreferences();

      expect(mockRecruitApiClient.get).toHaveBeenCalledWith(
        '/api/v4/ui_preferences?source=universal'
      );
    });
  });

  describe('postUserPreferences', () => {
    it('should call RecruitApiClient.post with correct parameters', () => {
      const params = {
        limit: 20,
        sort: 'lastname',
        sortOrder: 'desc'
      };

      const expectedPayload = {
        ui_preference: {
          row_count: 20,
          sorting: {
            sortBy: 'lastname',
            sortOrder: 'desc'
          },
          source: 'candidates'
        }
      };

      mockRecruitApiClient.post.mockResolvedValue({ data: {} });

      Api.postUserPreferences(params);

      expect(mockRecruitApiClient.post).toHaveBeenCalledWith(
        '/api/v4/ui_preferences',
        expectedPayload
      );
    });
  });

  describe('deleteCandidate', () => {
    it('should call RecruitApiClient.delete with correct candidate ID', () => {
      const candidateId = 123;

      mockRecruitApiClient.delete.mockResolvedValue({ data: {} });

      Api.deleteCandidate(candidateId);

      expect(mockRecruitApiClient.delete).toHaveBeenCalledWith('/api/v4/candidates/123');
    });

    it('should handle undefined candidate ID', () => {
      mockRecruitApiClient.delete.mockResolvedValue({ data: {} });

      Api.deleteCandidate();

      expect(mockRecruitApiClient.delete).toHaveBeenCalledWith('/api/v4/candidates/undefined');
    });
  });

  describe('updateCandidate', () => {
    it('should call RecruitApiClient.put with correct parameters', () => {
      const candidateId = 123;
      const params = {
        firstname: 'John',
        lastname: 'Doe',
        employee_id: 'EMP001',
        email: 'john.doe@example.com',
        linkedin_url: 'https://linkedin.com/in/johndoe',
        phone1: '1234567890',
        phone2: '0987654321',
        address_attributes: {
          street1: '123 Main St',
          street2: 'Apt 4B',
          suburb: 'Test City',
          postcode: '12345',
          street_number: '123',
          street_type: 'St',
          country_id: 1,
          state_id: 2
        }
      };

      const expectedPayload = { candidate: params };

      mockRecruitApiClient.put.mockResolvedValue({ data: {} });

      Api.updateCandidate(candidateId, params);

      expect(mockRecruitApiClient.put).toHaveBeenCalledWith(
        '/api/v4/candidates/123',
        expectedPayload
      );
    });
  });

  describe('getCountriesStates', () => {
    it('should call RecruitApiClient.get with correct endpoint', () => {
      mockRecruitApiClient.get.mockResolvedValue({ data: [] });

      Api.getCountriesStates();

      expect(mockRecruitApiClient.get).toHaveBeenCalledWith('/api/system/v1/countries_states.json');
    });
  });

  describe('API error handling', () => {
    it('should propagate errors from RecruitApiClient', async () => {
      const error = new Error('Network error');
      mockRecruitApiClient.get.mockRejectedValue(error);

      await expect(Api.getCandidates({} as any)).rejects.toThrow('Network error');
    });

    it('should handle different error types', async () => {
      const error = new Error('Validation failed');
      mockRecruitApiClient.post.mockRejectedValue(error);

      await expect(
        Api.postUserPreferences({
          limit: 10,
          sort: 'name',
          sortOrder: 'asc'
        })
      ).rejects.toThrow('Validation failed');
    });
  });

  describe('API response handling', () => {
    it('should return response data correctly', async () => {
      const mockResponse = {
        res: {
          candidates: [
            {
              id: 1,
              firstname: 'John',
              lastname: 'Doe',
              email: 'john@example.com'
            }
          ]
        },
        resHead: {
          'x-total-count': '1',
          'x-total-pages': '1'
        }
      };

      mockRecruitApiClient.get.mockResolvedValue(mockResponse);

      const result = await Api.getCandidates({
        page: 1,
        limit: 10,
        sort: 'firstname',
        sort_order: 'asc',
        'q[search]': ''
      });

      expect(result).toEqual(mockResponse);
    });
  });
});
